import React from 'react';
import { Box } from 'rebass';
import { Controller, Scene } from 'react-scrollmagic';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import Content from '../../components/Content/Content';
import BlogContentContainer from '../../components/BlogContentContainer/BlogContentContainer';
import WideContainer from '../../components/WideContainer/WideContainer';
import Markup from '../../base/Markup/Markup';
import SocialShare from '../../components/SocialShare/SocialShare';
import Typography, {
  TypographyContextProvider,
} from '../../components/Typography/Typography';

import { theme } from '../../styles/theme';
import colourMatching from '../../utils/colourMatching';
import createMemo from '../../utils/storyblokRelationsMemo';
import stringifyTeamMembers from '../../utils/stringifyTeamMembers';
import isFadeEnabled from '../../utils/isFadeEnabled';
import { NewsItemHeaderSectionStoryblok } from '../../types/storyblok';

dayjs.extend(advancedFormat);

const authorsMemo = createMemo();
const collaboratorsMemo = createMemo();

/**
 * Renders a <NewsItemHeaderSection /> component
 * @param {Object} props
 * @param {string} [props.authorLabel]
 * @param {string} [props.authors]
 * @param {string} [props.collaboratorsLabel]
 * @param {string} props.collaborators
 * @param {string} [props.colourScheme]
 * @param {string} [props.content]
 * @param {string} [props.publishedDate]
 * @param {string} [props.readTime]
 */
const NewsItemHeaderSection = ({
  authorLabel,
  authors: authorsRel,
  collaboratorsLabel,
  collaborators: collaboratorsRel,
  colourScheme,
  content,
  publishedDate,
  readTime,
}: NewsItemHeaderSectionStoryblok) => {
  const authors = authorsMemo(authorsRel);
  const collaborators = collaboratorsMemo(collaboratorsRel);

  const authorsText = stringifyTeamMembers(authors);
  const collaboratorsText = stringifyTeamMembers(collaborators);

  return (
    <TypographyContextProvider value={{ colourScheme }}>
      <Box
        as="section"
        py="50px"
        backgroundColor={colourMatching(colourScheme)}
      >
        <Controller globalSceneOptions={{ triggerHook: 'onEnter' }}>
          <Scene classToggle="full-opacity">
            <Box className={isFadeEnabled() ? 'zero-opacity' : ''}>
              <WideContainer
                sx={{ '&&&': { maxWidth: '1550px' }, position: 'relative' }}
                className="!max-w-standard"
              >
                <Box className="left-0 block pl-9 md:absolute">
                  <SocialShare
                    colourScheme={colourScheme}
                    size={25}
                    withBackground
                  />
                </Box>
                <BlogContentContainer className="relative">
                  <Content colourScheme={colourScheme}>
                    <Box
                      mt={[5, , , , 0]}
                      sx={{ '&>p': { margin: `${theme.space[3]} 0` } }}
                    >
                      {authorsText ? (
                        <Typography>
                          <Typography component="strong">
                            {authorLabel}
                          </Typography>
                          &nbsp;
                          {authorsText}
                        </Typography>
                      ) : null}
                      {collaboratorsText ? (
                        <Typography>
                          <Typography component="strong">
                            {collaboratorsLabel}
                          </Typography>
                          &nbsp;
                          {collaboratorsText}
                        </Typography>
                      ) : null}
                      <Typography>
                        {dayjs(publishedDate).format('MMMM Do YYYY')}
                        {readTime ? `  /  ${readTime}` : ''}
                      </Typography>
                    </Box>
                    {content && <Markup content={content} />}
                  </Content>
                </BlogContentContainer>
              </WideContainer>
            </Box>
          </Scene>
        </Controller>
      </Box>
    </TypographyContextProvider>
  );
};

export default NewsItemHeaderSection;
